// Content script for page injection and extension messaging
import { isDirectMediaFile, isSandboxed } from './utils';
// Content script runs per-tab and may persist across same-origin navigation,
// but we track page ID to handle URL changes within the same script instance
let currentPageId = null;
// Handle messages from background script
const handleBackgroundMessage = (request, _sender, sendResponse) => {
    try {
        // Forward extension signals to the page context via window.postMessage
        if (request.type &&
            ['RPC_RESULT', 'RPC_ERROR', 'EXTENSION_ERROR', 'EXTENSION_READY'].includes(request.type)) {
            window.postMessage(request, window.location.origin);
            sendResponse({ success: true });
        }
        else {
            sendResponse({ success: false, error: 'Unknown action' });
        }
    }
    catch (error) {
        sendResponse({
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
        });
    }
    return true; // Keep message channel open for async response
};
// Handle messages from page context (injected script)
const handlePageMessage = (event) => {
    // This is to make Snyk happy, not really needed for this use case.
    if (event.origin !== window.location.origin || event.source !== window) {
        return;
    }
    const data = event.data;
    if (!data || typeof data !== 'object' || data === null) {
        return;
    }
    if (data.action === 'proxyRpcCall') {
        if (data.pageId && data.method && data.requestId) {
            chrome.runtime.sendMessage(data);
        }
    }
};
// Set up event listeners
chrome.runtime.onMessage.addListener(handleBackgroundMessage);
window.addEventListener('message', handlePageMessage);
// Send CSP violations to the extension for analytics
window.addEventListener('securitypolicyviolation', (e) => {
    chrome.runtime
        .sendMessage({
        action: 'cspViolation',
        data: {
            pageId: currentPageId,
            url: window.location.href,
            violatedDirective: e.violatedDirective,
        },
    })
        .catch(() => {
        console.debug('Failed to send CSP violation message');
    });
});
// Store pageId and extension ID in a way the injected script can access it
function storePageIdForInjectedScript(pageId) {
    // Store page ID and extension ID using data attributes (CSP-compliant)
    // This is safe since page ID is not sensitive (just a UUID) and extension ID is public
    document.documentElement.setAttribute('data-windsurf-page-id', pageId);
    document.documentElement.setAttribute('data-windsurf-extension-id', chrome.runtime.id);
}
// Inject Parent Injection Script into Page Context without inline scripts
function injectParentScript(pageId) {
    // Check if already injected
    if (document.getElementById('windsurf-parent-injection-script')) {
        return;
    }
    currentPageId = pageId;
    // Store pageId BEFORE injecting the script so it's available when script loads
    storePageIdForInjectedScript(pageId);
    const script = document.createElement('script');
    script.id = 'windsurf-parent-injection-script';
    script.src = chrome.runtime.getURL('parent_injection.js');
    script.type = 'text/javascript';
    // Helper function to report injection failures
    const reportInjectionFailure = () => {
        chrome.runtime
            .sendMessage({
            action: 'scriptInjectionFailed',
            data: { pageId, url: window.location.href },
        })
            .catch(() => {
            console.debug('Failed to send injection failed message');
        });
    };
    // Set up error handler for script loading failures
    script.onerror = reportInjectionFailure;
    // Helper function to safely append script
    const safeAppendScript = (targetElement) => {
        try {
            targetElement.appendChild(script);
        }
        catch (error) {
            reportInjectionFailure();
        }
    };
    // Inject script immediately if possible, otherwise wait for DOM
    const target = document.head || document.documentElement;
    if (target) {
        safeAppendScript(target);
    }
    else {
        document.addEventListener('DOMContentLoaded', () => {
            const deferredTarget = document.head || document.documentElement;
            safeAppendScript(deferredTarget);
        });
    }
}
// Register page with background service worker
async function registerPageWithBackground(pageId) {
    const registrationData = {
        pageId: pageId,
        url: window.location.href,
        timestamp: Date.now(),
    };
    try {
        await sendToExtensionBackground('registerPage', registrationData);
    }
    catch (error) {
        console.debug('Failed to register page', error);
    }
}
// Send message to extension background
async function sendToExtensionBackground(action, data) {
    try {
        const response = await chrome.runtime.sendMessage({ action, data });
        if (response?.success) {
            return;
        }
        else {
            throw new Error(response?.error || 'Unknown error');
        }
    }
    catch (error) {
        throw new Error(error instanceof Error ? error.message : 'Unknown error');
    }
}
// Skip injection on direct media files or sandboxed contexts
(async () => {
    if (!(await isDirectMediaFile()) && !isSandboxed()) {
        // Generate unique page ID for this page
        const pageId = crypto.randomUUID();
        // Start injection when DOM is ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => injectParentScript(pageId));
        }
        else {
            injectParentScript(pageId);
        }
        // Register this page with the background script for tracking
        registerPageWithBackground(pageId);
    }
})();
